import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, BuildingIcon, UserIcon, CopyIcon, CheckIcon, Trash2Icon, LinkIcon, ClockIcon, UploadIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import { Supplier, mockSuppliers, InvitedSupplier, mockInvitedSuppliers } from '../mocks/mockSuppliers';
export function Suppliers() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'owed' | 'invited'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [invitedSuppliers, setInvitedSuppliers] = useState<InvitedSupplier[]>(mockInvitedSuppliers);
  const filteredSuppliers = mockSuppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) || supplier.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'owed' && supplier.amountOwed !== `${getTenant().currency.code} 0.00`;
    const matchesArchived = showArchived ? true : !supplier.isArchived;
    return matchesSearch && matchesTab && matchesArchived;
  });
  const filteredInvited = invitedSuppliers.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleCopyInviteLink = (supplier: InvitedSupplier) => {
    const fullLink = `${window.location.origin}${supplier.inviteLink}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedId(supplier.id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const handleDeleteInvite = (id: string) => {
    setInvitedSuppliers(prev => prev.filter(s => s.id !== id));
  };
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-14190" data-mp="11zyc9a8da9sj">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-14191" data-mp="323r60yg635i0">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-14192" data-mp="32150t5gr122y">
          {t('pages.Suppliers.suppliers')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-14193" data-mp="3bt2d6fjhu039">
          <button onClick={() => navigate('/supplier-bulk-upload')} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-14194" data-mp="3budgke9115tu">
            <UploadIcon size={16} className="mr-2" data-id="element-14195" data-mp="2o2y9672e7ybq" />
            {t('pages.Suppliers.upload')}
          </button>
          <button onClick={() => navigate('/supplier-form')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-14196" data-mp="2t2b5rjj2gjri">
            {t('pages.Suppliers.add_supplier')}
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-14197" data-mp="cg0ag75ifwh0">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-14198" data-mp="1sucs5ie7jzmh">
          {/* Tabs */}
          <div className="flex space-x-6" role="tablist" aria-label="Supplier tabs" data-id="element-14199" data-mp="1o2mh1a7g57u0">
            <button role="tab" aria-selected={activeTab === 'all'} aria-controls="suppliers-tabpanel" id="suppliers-tab-all" onClick={() => setActiveTab('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'all' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-14200" data-mp="1j2gvmbrrshkd">
              {t('pages.Suppliers.all')}
            </button>
            <button role="tab" aria-selected={activeTab === 'owed'} aria-controls="suppliers-tabpanel" id="suppliers-tab-owed" onClick={() => setActiveTab('owed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'owed' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-14201" data-mp="2zggqh8brdh0r">
              {t('pages.Suppliers.money_owed_to')}
            </button>
            <button role="tab" aria-selected={activeTab === 'invited'} aria-controls="suppliers-tabpanel" id="suppliers-tab-invited" onClick={() => setActiveTab('invited')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invited' ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-14202" data-mp="18ht5h7bpaljz">
              {t('pages.Suppliers.invited')}
              {invitedSuppliers.length > 0 && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${activeTab === 'invited' ? 'bg-[#E2231A]/10 text-[#9F140F]' : 'bg-gray-100 text-gray-500'}`} data-id="element-14203" data-mp="awrz5qe0fal6">
                  {invitedSuppliers.length}
                </span>}
            </button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center space-x-6" data-id="element-14204" data-mp="fujn69bjack3">
            {activeTab !== 'invited' && <label className="flex items-center cursor-pointer" data-id="element-14205" data-mp="ttt5pfn7pir8">
                <span className="text-sm text-gray-600 mr-3 font-medium" data-id="element-14206" data-mp="ovhvh3t0968h">
                  {t('pages.Suppliers.show_archived_suppliers')}
                </span>
                <div className="relative" data-id="element-14207" data-mp="ymcf00ev9rq8">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-14208" data-mp="ynnkc4nyn7jg" />

                  <div className={`block w-10 h-6 rounded-full transition-colors ${showArchived ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-14209" data-mp="18iflfigr4ciz"></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-14210" data-mp="1knbbdcvxcoao"></div>
                </div>
              </label>}

            <div className="relative w-64" data-id="element-14211" data-mp="1yeq0m1gfdlq6">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-14212" data-mp="1ydyericmr2d3" />

              <input type="search" aria-label={t('pages.Suppliers.attrs.search_by_supplier')} placeholder={t('pages.Suppliers.attrs.search_by_supplier')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14213" data-mp="28c4ebtayrwes" />
            </div>
          </div>
        </div>

        {/* Active Suppliers Table */}
        {activeTab !== 'invited' && <div className="overflow-x-auto" data-id="element-14214" data-mp="2nfnn58r5fta7">
            <table className="w-full text-left border-collapse" data-id="element-14215" data-mp="2igkt0jcpj7h0">
              <thead data-id="element-14216" data-mp="2ie82hhsvvosc">
                <tr className="border-b border-gray-100" data-id="element-14217" data-mp="3f6lh0g595jx9">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14218" data-mp="3f61xww3v8phn">
                    {t('pages.Suppliers.supplier_detail')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14219" data-mp="364luj8bgxnmx">
                    {t('pages.BulkSupplierUpload.type')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14220" data-mp="1urrssap1ydgl">
                    {t('pages.Suppliers.you_owe_them')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14221" data-mp="3ptdcxfzhw56w">
                    {t('pages.SupplierDetail.source')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-14222" data-mp="3fvqn3ygnqt85">
                    {t('pages.SupplierDetail.date_created')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-14223" data-mp="cg1p1f6somjy">
                {filteredSuppliers.map(supplier => <tr key={supplier.id} onClick={() => navigate(`/supplier-detail/${supplier.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-14224" data-mp="hdjzxb4dhbul">
                    <td className="px-6 py-4" data-id="element-14225" data-mp="ve4ge0mtevkx">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-14226" data-mp="vcbaink10fdz">
                        {supplier.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5" data-id="element-14227" data-mp="2ktox5w5us3lj">
                        {supplier.email}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-14228" data-mp="2ukjjt1n0h57i">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-14229" data-mp="2plgdzez7iuyh">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-14230" data-mp="21xytm4i9uz5j" />
                          {t('pages.SupplierDetail.company')}
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-14231" data-mp="2aupnca9cq1wy">
                          <UserIcon size={12} className="mr-1.5" data-id="element-14232" data-mp="3manzimztt42e" />
                          {t('pages.SupplierDetail.individual')}
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-14233" data-mp="2j9fon0wr7a9j">
                      <div className="text-sm font-medium text-gray-900" data-id="element-14234" data-mp="2f8lzk47krpf2">
                        {supplier.amountOwed}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-14235" data-mp="2afj23kzofvtl">
                      <div className="text-sm text-gray-500" data-id="element-14236" data-mp="25jc54hp9duns">
                        {supplier.source}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-14237" data-mp="20h3bdj4kaokm">
                      <div className="text-sm text-gray-500" data-id="element-14238" data-mp="1vjlc6kgcjnpt">
                        {supplier.dateCreated}
                      </div>
                    </td>
                  </tr>)}
                {filteredSuppliers.length === 0 && <tr data-id="element-14239" data-mp="3lwsnwe6a2zy2">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-14240" data-mp="3lx2ea2v8j94a">
                      {t('pages.Suppliers.no_suppliers_found_matching_your_criteria')}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}

        {/* Invited Suppliers Table */}
        {activeTab === 'invited' && <div className="overflow-x-auto" data-id="element-14241" data-mp="710f8t0ti2yp">
            <table className="w-full text-left border-collapse" data-id="element-14242" data-mp="72tidyc8251y">
              <thead data-id="element-14243" data-mp="35jo1jqr8n7bb">
                <tr className="border-b border-gray-100" data-id="element-14244" data-mp="35k786bbfu9a1">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14245" data-mp="3agnync640zqa">
                    {t('pages.Suppliers.supplier_name')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14246" data-mp="3jine401h7vr4">
                    {t('pages.BulkSupplierUpload.type')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14247" data-mp="yrwo7ijrbngp">
                    {t('pages.Suppliers.status')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14248" data-mp="2vundnkrrv4rf">
                    {t('pages.Suppliers.invited')}
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-14249" data-mp="34spb5qcarf9y">
                    {t('pages.Suppliers.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-14250" data-mp="2c278gqfhjfx2">
                {filteredInvited.map(supplier => <tr key={supplier.id} className="group" data-id="element-14251" data-mp="2lu38w851ni4g">
                    <td className="px-6 py-4" data-id="element-14252" data-mp="2lvo47zz6n53d">
                      <div className="text-sm font-medium text-gray-900" data-id="element-14253" data-mp="2h34qn30hw9yk">
                        {supplier.name}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-14254" data-mp="1tbzaemqn7fnq">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-14255" data-mp="3wyeogsjxc5d">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-14256" data-mp="8p7ydf32pfz7" />
                          {t('pages.SupplierDetail.company')}
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-14257" data-mp="3w1is6qp5mg5g">
                          <UserIcon size={12} className="mr-1.5" data-id="element-14258" data-mp="rn11oihlzrx4" />
                          {t('pages.SupplierDetail.individual')}
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-14259" data-mp="il1k9p2omltn">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-14260" data-mp="inxi7dej5ai3">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-14261" data-mp="33cuzwb5zmqie"></span>
                        {t('pages.Suppliers.pending')}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-14262" data-mp="3dtff2p8fds53">
                      <div className="text-sm text-gray-900" data-id="element-14263" data-mp="39t56r5x5upn2">
                        {supplier.dateInvited}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1" data-id="element-14264" data-mp="34vlhzc2u6ugv">
                        <ClockIcon size={11} data-id="element-14265" data-mp="5e5fpovx7110" />
                        {supplier.daysAgo} {t('pages.Suppliers.days_ago')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-14266" data-mp="3sjd6de1nixyi">
                      <div className="flex items-center justify-end gap-2" data-id="element-14267" data-mp="3slzd5bgpx9fo">
                        <button onClick={() => handleCopyInviteLink(supplier)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-14268" data-mp="3nk0bc60mmkku">
                          {copiedId === supplier.id ? <>
                              <CheckIcon size={13} className="mr-1.5 text-green-600" data-id="element-14269" data-mp="2712eqfwe0vks" />

                              {t('pages.SupplierForm.copied')}
                            </> : <>
                              <CopyIcon size={13} className="mr-1.5" data-id="element-14270" data-mp="2pr0m0tsd2knd" />
                              {t('pages.Suppliers.copy_link')}
                            </>}
                        </button>
                        <button onClick={() => handleDeleteInvite(supplier.id)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors" data-id="element-14271" data-mp="2zpq7ewjgun8d">
                          <Trash2Icon size={13} className="mr-1.5" data-id="element-14272" data-mp="xazo00zor6bv" />
                          {t('pages.Suppliers.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>)}
                {filteredInvited.length === 0 && <tr data-id="element-14273" data-mp="128j2n20apl4c">
                    <td colSpan={5} className="px-6 py-12 text-center text-sm" data-id="element-14274" data-mp="1g9lrtnml7bjh">
                      <div className="flex flex-col items-center gap-3" data-id="element-14275" data-mp="1g7ipc448zpxv">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-14276" data-mp="1b9qwn7vs3zsm">
                          <LinkIcon size={20} className="text-gray-400" data-id="element-14277" data-mp="1pyn5v89kniut" />
                        </div>
                        <div data-id="element-14278" data-mp="1l13qg515101a">
                          <p className="text-gray-700 font-medium" data-id="element-14279" data-mp="1z036ioal46lu">
                            {t('pages.Suppliers.no_pending_invitations')}
                          </p>
                          <p className="text-gray-500 mt-0.5" data-id="element-14280" data-mp="1uwew7xukiwww">
                            {t('pages.Suppliers.invite_a_supplier_by_clicking_add')}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}